import { type VaultItem, type VaultLoginPayload } from '../types'

/**
 * セキュリティレポート分析関数群 (T-D-Reports)。
 *
 * 設計判断:
 *   - 全関数は pure function (VaultItem[] → ReportItem[]) — 副作用なし。
 *   - zxcvbn 等の辞書依存ライブラリを避け、長さ + 複雑さカテゴリのスコアで簡易判定。
 *   - 外部 API / ドメインへの通信は行わない (ゼロ知識方針)。漏洩 DB 照合 (HIBP 等) は
 *     非対応。
 */

export interface ReportItem {
  readonly id: string
  readonly name: string
  readonly username?: string
  readonly url?: string
}

type LoginItem = VaultItem & { payload: VaultLoginPayload }

/**
 * 「弱いパスワード」の唯一の判定基準 (TASK-WEB-001)。
 * 長さ < 10 を弱いとみなす。HomePage の security card と SecurityReport が
 * 同じ数値を出すよう、両者ともこの定義を共有する (定義の二重化を防ぐ)。
 */
export const WEAK_PASSWORD_MIN_LENGTH = 10

export const isWeakPassword = (pw: string): boolean =>
  pw.length > 0 && pw.length < WEAK_PASSWORD_MIN_LENGTH

const toReportItem = (item: LoginItem): ReportItem => ({
  id: item.id,
  name: item.payload.name,
  username: item.payload.username,
  url: item.payload.url,
})

/** 弱いパスワード (length < 10) の login アイテムを返す。 */
export const analyzeWeak = (items: readonly VaultItem[]): readonly ReportItem[] =>
  items
    .filter(
      (item): item is LoginItem =>
        item.payload.type === 'login' &&
        !!item.payload.password &&
        isWeakPassword(item.payload.password),
    )
    .map(toReportItem)

/** 再利用パスワード (複数アイテムで同一パスワードを使用) の login アイテムを返す。 */
export const analyzeReused = (items: readonly VaultItem[]): readonly ReportItem[] => {
  const loginItems = items.filter(
    (item): item is LoginItem => item.payload.type === 'login' && !!item.payload.password,
  )

  const byPw = new Map<string, LoginItem[]>()
  for (const item of loginItems) {
    const pw = item.payload.password
    if (!pw) continue
    const group = byPw.get(pw) ?? []

    group.push(item)
    byPw.set(pw, group)
  }
  const result: ReportItem[] = []
  for (const group of byPw.values()) {
    if (group.length < 2) continue
    for (const item of group) {
      result.push(toReportItem(item))
    }
  }

  return result
}

/** http:// (非暗号化) URL を持つ login アイテムを返す。 */
export const analyzeUnsecured = (items: readonly VaultItem[]): readonly ReportItem[] =>
  items
    .filter(
      (item): item is LoginItem =>
        item.payload.type === 'login' &&
        typeof item.payload.url === 'string' &&
        item.payload.url.startsWith('http://'),
    )
    .map(toReportItem)

/** TOTP シークレット未設定の login アイテムを返す。 */
export const analyzeInactive2fa = (items: readonly VaultItem[]): readonly ReportItem[] =>
  items
    .filter((item): item is LoginItem => item.payload.type === 'login' && !item.payload.totpSecret)
    .map(toReportItem)
